import { Card, Grid, Typography, Box } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { PageTitle } from '../../../../components/PageTitle';
import Loader from '../../../../components/Loader';
import ErrorBanner from '../../../../components/errorhandling/ErrorBanner';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { useAgreements } from '../../../../hooks/api/useAgreements';
import { masterlistCheckQueryKey } from '../../../../hooks/api/useMasterlistCheck';
import { Agreements } from '../../../../types/Agreements.types';
import { isApiError } from '../../../../types/Error';
import { Journals } from '../../../../types/Journals.types';
import {
  ErrorObject,
  errorSeverity,
} from '../../../../types/errorhandling.types';
import { JournalsContainer } from '../journals/JournalContainer';
import { AgreementFormFields } from './AgreementFormFields';
import { CopyAgreementModal } from './CopyAgreementModal';
import { DeleteAgreementModal } from './DeleteAgreementModal';
import { FormDataType } from './types';

const initialFormData: FormDataType = {
  name: '',
  publisher: '',
  payment: '',
  start_date: '',
  end_date: '',
  payment_url: '',
  file: '',
  description: {},
  notes: '',
  extra_info: '',
  licenses: [],
  status: 'enabled',
  collections: [],
};

export const AgreementForm = ({ agreement }: { agreement?: Agreements }) => {
  const { t } = useTranslation();
  const { loggedInAccount } = useAuthContext();
  const [loading, setLoading] = React.useState<boolean>(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [journals, setJournals] = useState<Journals[]>([]);
  const [previewJournals, setPreviewJournals] = useState<Journals[]>([]);
  const [error, setError] = useState<string>();
  const [errors, setErrors] = useState<ErrorObject>();
  const [publishers, setPublishers] = useState<string[]>([]);
  const [paymentOptions, setPaymentOptions] = useState<string[]>([]);
  const [selectableLicenses, setSelectableLicenses] = useState<string[]>([]);
  const [currentAgreement, setCurrentAgreement] = useState(agreement);
  const [formData, setFormData] = useState<FormDataType>(initialFormData);
  // The form keeps the picked File after a save, so remember which one was imported: sending it
  // again would re-import the journals under new ids.
  const importedFileRef = useRef<File | undefined>(undefined);
  const { agreementId } = useParams<{ agreementId: string }>();
  const {
    addAgreement,
    editAgreement,
    fetchPayments,
    fetchPublishers,
    fetchLicenses,
  } = useAgreements();

  const onPreviewJournals = useCallback((journals: Journals[]) => {
    setPreviewJournals(journals);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const selectablePublishers = await fetchPublishers();
      const selectablePaymentOptions = await fetchPayments();
      const selectableLicenses = await fetchLicenses();
      setPublishers(selectablePublishers);
      setPaymentOptions(selectablePaymentOptions);
      setSelectableLicenses(selectableLicenses);
    };

    fetchData().catch(console.error);

    if (!agreementId || !agreement) return;

    setJournals(agreement.journals ?? []);
  }, [
    agreementId,
    agreement,
    fetchPublishers,
    fetchPayments,
    fetchLicenses,
    // Removed 'branding' and 'payments' - they're logged but not used in the effect
  ]);

  const isAdding = !agreementId;

  const brokenJournalCount = useMemo(() => {
    const brokenJournals = journals?.filter((journal: Journals) => {
      return (
        !journal.name ||
        !journal.publishing_model ||
        journal.licenses?.length === 0 ||
        !journal.payment
      );
    });
    return brokenJournals?.length ?? 0;
  }, [journals]);

  const onChangeHandler = useCallback((responseBody: FormDataType) => {
    setFormData(responseBody);
    setErrors(undefined);
    setError(undefined);
  }, []);

  const onSubmitHandler = useCallback(
    async (updatedForm: FormDataType) => {
      // A picked file is a File; the field is '' otherwise (typed as string).
      const pickedFile =
        typeof updatedForm.file === 'object'
          ? (updatedForm.file as unknown as File)
          : undefined;
      const newFile =
        pickedFile !== importedFileRef.current ? pickedFile : undefined;
      setLoading(true);
      setFormData(updatedForm);
      setErrors(undefined);
      setError(undefined);
      const requestBody: FormData = new FormData();
      requestBody.append('name', updatedForm.name);
      requestBody.append('publisher', updatedForm.publisher);
      requestBody.append('start_date', updatedForm.start_date);
      requestBody.append('end_date', updatedForm.end_date);
      requestBody.append('payment_url', updatedForm.payment_url);
      requestBody.append('file', newFile ?? '');
      requestBody.append('payment', updatedForm.payment);
      requestBody.append(
        'description',
        JSON.stringify(updatedForm.description)
      );
      requestBody.append('notes', updatedForm.notes);
      requestBody.append('extra_info', updatedForm.extra_info);
      requestBody.append('status', updatedForm.status);
      updatedForm.licenses.forEach((license) => {
        requestBody.append('licenses[]', license);
      });
      updatedForm.collections.forEach((collection) =>
        requestBody.append('collections[]', collection)
      );
      if (isAdding) {
        try {
          const response = await addAgreement(requestBody);
          navigate(`/agreements/${response._id}`);
        } catch (e: unknown) {
          if (isApiError(e)) {
            setErrors(e);
          } else {
            setError(
              'Something went wrong when creating the agreement. Please try again.'
            );
          }
        }
      } else if (!isAdding && agreementId) {
        try {
          const response = await editAgreement(requestBody, agreementId);
          setCurrentAgreement((prev) => {
            return {
              ...(prev ?? ({} as Agreements)),
              ...response,
              journals: response.journals ?? prev?.journals ?? [],
            };
          });
          setJournals((prev) => response.journals ?? prev);
          if (newFile) importedFileRef.current = newFile;
          // A re-upload replaces the journals (and their ids), so drop the old results instead of
          // showing them against the new ids while refetching. Other edits keep the journals; the
          // publisher can still change the title matches, so refetch in the background.
          const checkKey = { queryKey: masterlistCheckQueryKey(agreementId) };
          if (newFile) {
            queryClient.resetQueries(checkKey);
          } else {
            queryClient.invalidateQueries(checkKey);
          }
          navigate(`/agreements/${agreementId}`);
        } catch (e: unknown) {
          if (isApiError(e)) {
            setErrors(e);
          } else {
            setError(
              'Something went wrong when editing the agreement. Please try again.'
            );
          }
        }
      }
      setLoading(false);
    },
    [addAgreement, editAgreement, isAdding, agreementId, navigate, queryClient]
  );

  if (
    (!isAdding && !agreement) ||
    (isAdding && loading) ||
    !publishers ||
    !paymentOptions ||
    !selectableLicenses
  ) {
    return <Loader />;
  }

  return (
    <>
      <PageTitle
        title={
          isAdding
            ? (t('agreements.createAgreement') as string)
            : (t('agreements.editAgreement') as string)
        }
      />
      {brokenJournalCount > 0 && (
        <ErrorBanner
          errorMessage="One or more of this agreements journal entries are missing vital data."
          severity={errorSeverity.Warning}
          layer={1}
        />
      )}
      {error && (
        <ErrorBanner
          errorMessage={error}
          severity={errorSeverity.Error}
          layer={brokenJournalCount > 0 ? 2 : 1}
        />
      )}
      {errors && (
        <ErrorBanner
          errors={errors}
          severity={errorSeverity.Error}
          layer={brokenJournalCount > 0 ? (error ? 3 : 2) : error ? 2 : 1}
        />
      )}
      <Card variant="default" className="mb-1.5">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 10,
            mb: 16,
          }}
        >
          <Box>
            <Typography component="h1" variant="h4" gutterBottom>
              {isAdding
                ? t('agreements.createAgreement')
                : t('agreements.editAgreement')}
            </Typography>
            <Typography variant="body1">
              {t('agreements.asteriskRequired')}
            </Typography>
          </Box>
          {!isAdding && (
            <Box
              sx={{
                display: 'flex',
                gap: 8,
                flexDirection: { xs: 'column', sm: 'row' },
              }}
            >
              {loggedInAccount === 'admin' && (
                <CopyAgreementModal id={agreementId!} />
              )}
              <DeleteAgreementModal
                id={agreementId!}
                journalMode={false}
                journalId={undefined}
              />
            </Box>
          )}
        </Box>
        <Grid item sx={{ width: '100%' }}>
          <AgreementFormFields
            isAdding={isAdding}
            agreementId={agreementId}
            agreement={currentAgreement}
            loading={loading}
            onPreviewJournals={onPreviewJournals}
            onSubmit={onSubmitHandler}
            onChange={onChangeHandler}
            publishers={publishers}
            paymentOptions={paymentOptions}
            selectableLicenses={selectableLicenses}
            initialFormData={formData}
          />
        </Grid>
      </Card>
      <JournalsContainer
        agreement={agreement}
        isAdding={isAdding}
        journals={journals}
        previewJournals={previewJournals}
      />
    </>
  );
};
